// src/components/ui/PageShell/PageShell.tsx
// Macro layout shell following IonicLayout.md standards
// Provides the standard IonPage > Header > IonContent structure

import React, { forwardRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Header, type HeaderType, type BackgroundStyle, type TextAlign } from '../Header';
import './PageShell.css';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface PageShellProps {
    /** Page title displayed in header */
    title: string;
    /** Optional subtitle */
    subtitle?: string;
    /** Page content */
    children: React.ReactNode;

    // ─── Header Configuration ──────────────────────────────────────

    /** Header type: 'home' for logo, 'page' for title (default: 'page') */
    headerType?: HeaderType;
    /** Title alignment */
    titleAlign?: TextAlign;
    /** Header background style */
    background?: BackgroundStyle;
    /** Hide the header entirely */
    hideHeader?: boolean;

    // ─── Navigation ────────────────────────────────────────────────

    /** Show back button */
    showBack?: boolean;
    /** Custom back action (defaults to history.goBack) */
    onBack?: () => void;

    // ─── Header Actions ────────────────────────────────────────────

    /** Show search icon */
    showSearch?: boolean;
    /** Search click handler */
    onSearch?: () => void;
    /** Show filter icon */
    showFilter?: boolean;
    /** Filter click handler */
    onFilter?: () => void;
    /** Show more options icon */
    showMore?: boolean;
    /** More options click handler */
    onMore?: () => void;
    /** Show notifications icon */
    showNotifications?: boolean;
    /** Notification count badge */
    notificationCount?: number;
    /** Notifications click handler */
    onNotifications?: () => void;
    /** Show settings icon */
    showSettings?: boolean;
    /** Custom right-side buttons */
    rightButtons?: React.ReactNode;

    // ─── Content Configuration ─────────────────────────────────────

    /** Enable iOS large title collapse effect */
    fullscreen?: boolean;
    /**
     * Content to render before main children (direct child of IonContent).
     * Use for IonRefresher, IonInfiniteScroll, etc.
     * @example
     * renderBeforeContent={
     *   <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
     *     <IonRefresherContent />
     *   </IonRefresher>
     * }
     */
    renderBeforeContent?: React.ReactNode;
    /**
     * Content to render after main children (direct child of IonContent).
     * Use for IonInfiniteScroll at bottom.
     */
    renderAfterContent?: React.ReactNode;
    /**
     * Disable default padding on IonContent.
     * Use when content manages its own padding (e.g., full-bleed lists).
     */
    noPadding?: boolean;

    // ─── Escape Hatches ────────────────────────────────────────────

    /**
     * ESCAPE HATCH: Disable IonContent wrapper.
     *
     * USE ONLY when integrating libraries that require scroll control
     * (e.g., react-window, react-virtualized, react-grid-layout).
     *
     * CONSEQUENCES:
     * - Pull-to-refresh (IonRefresher) will not work
     * - iOS rubber-band scrolling disabled
     * - Keyboard avoidance may break
     * - Large title collapse effect disabled
     * - Safe area handling becomes your responsibility
     *
     * @see IonicLayout.md Appendix C for escape hatch conventions
     */
    disableScrollWrapper?: boolean;

    /**
     * Use IonContent but disable its scroll behavior.
     * This is a safer alternative to disableScrollWrapper that still maintains
     * Ionic's flex layout but lets you handle scroll on an inner container.
     *
     * Use this when you need custom scroll but want proper tab bar spacing.
     */
    disableContentScroll?: boolean;

    // ─── HTML Attributes ───────────────────────────────────────────

    /** Additional className for IonPage */
    className?: string;
    /** Inline styles for IonPage */
    style?: React.CSSProperties;
    /** Data attributes for testing/debugging */
    'data-testid'?: string;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const PageShell = forwardRef<HTMLElement, PageShellProps>(
    (
        {
            title,
            subtitle,
            children,
            // Header
            headerType = 'page',
            titleAlign = 'left',
            background = 'default',
            hideHeader = false,
            // Navigation
            showBack = false,
            onBack,
            // Header actions
            showSearch = false,
            onSearch,
            showFilter = false,
            onFilter,
            showMore = false,
            onMore,
            showNotifications = false,
            notificationCount,
            onNotifications,
            showSettings = false,
            rightButtons,
            // Content
            fullscreen = false,
            renderBeforeContent,
            renderAfterContent,
            noPadding = false,
            // Escape hatch
            disableScrollWrapper = false,
            disableContentScroll = false,
            // HTML
            className,
            style,
            'data-testid': testId,
        },
        ref
    ) => {
        return (
            <IonPage
                ref={ref}
                className={className}
                style={style}
                data-layout="PageShell"
                data-testid={testId}
            >
                {/* Header - uses UI library component */}
                {!hideHeader && (
                    <Header
                        type={headerType}
                        title={title}
                        subtitle={subtitle}
                        titleAlign={titleAlign}
                        background={{ style: background }}
                        showBack={showBack}
                        onBack={onBack}
                        showSearch={showSearch}
                        onSearch={onSearch}
                        showFilter={showFilter}
                        onFilter={onFilter}
                        showMore={showMore}
                        onMore={onMore}
                        showNotifications={showNotifications}
                        notificationCount={notificationCount}
                        onNotifications={onNotifications}
                        showSettings={showSettings}
                        rightButtons={rightButtons}
                    />
                )}

                {/* Content - with optional escape hatch */}
                {disableScrollWrapper ? (
                    // Full escape hatch: caller manages everything
                    children
                ) : (
                    <IonContent
                        fullscreen={fullscreen}
                        scrollY={!disableContentScroll}
                        className={noPadding ? undefined : 'ion-padding'}
                        style={disableContentScroll ? {
                            '--overflow': 'hidden',
                        } as React.CSSProperties : undefined}
                    >
                        {/* Direct children of IonContent (refresher, etc.) */}
                        {renderBeforeContent}

                        {/* Main content */}
                        {children}

                        {/* After content (infinite scroll, etc.) */}
                        {renderAfterContent}
                    </IonContent>
                )}
            </IonPage>
        );
    }
);

PageShell.displayName = 'PageShell';

export default PageShell;
